import React, { useRef, useState, useMemo } from 'react';
import { PageHeader as AntHeader } from '@ant-design/pro-layout';
import { Menu, Dropdown, Drawer, Avatar, Modal, Spin, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DownOutlined, MailOutlined, LoadingOutlined, SwapOutlined, CalendarOutlined } from '@ant-design/icons';
import { FiLogOut, FiUser, FiLock, FiSettings } from 'react-icons/fi';
import { CloudMoon, CloudSun } from 'lucide-react';
import { getFirstLetterFromWords } from 'helpers';
import { SIDE_MENUS, SUPER_ADMIN_EMAILS } from 'constants/app-constants';
import { setSelectedBranch } from 'redux/reducers/globals/globalActions';
import { applicationLogout } from 'services/LoginServices';
import { useI18n } from 'hooks/useI18n';
import { useTheme } from 'hooks/useTheme';
import SecurityTimer from './security-timer/SecurityTimer';
import './drawer.scss';
import './page-header.scss';

// Function to translate menu names
const getMenuTranslation = (menuName, t) => {
	const menuTranslations = {
		Dashboard: t('navigation.dashboard'),
		Customers: t('navigation.customers'),
		'Bulk Upload': t('navigation.bulk_upload'),
		Visitors: t('navigation.visitors'),
		'Personal Training': t('navigation.personal_training'),
		Products: t('navigation.products'),
		Coaches: t('navigation.coaches'),
		Payments: t('navigation.payments'),
		Outstanding: t('navigation.outstanding'),
		'Product Expense': t('navigation.product_expense'),
		Masters: t('navigation.masters'),
		'Diet Plan': t('navigation.diet_plan'),
		Reports: t('navigation.reports'),
		Settings: t('navigation.settings'),
		'Password Setup': t('navigation.password_setup'),
		Profile: t('navigation.profile'),
		Logout: t('navigation.logout'),
		// Add more menu translations as needed
	};
	return menuTranslations[menuName] || menuName;
};

// Function to calculate remaining days
const calculateRemainingDays = (nextBillDate) => {
	if (!nextBillDate) return null;
	
	const currentDate = new Date();
	const billDate = new Date(nextBillDate);
	
	if (isNaN(billDate.getTime())) return null;
	
	const timeDifference = billDate.getTime() - currentDate.getTime();
	const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
	
	return daysDifference;
};

// Function to get status color based on remaining days
const getStatusColor = (days) => {
	if (days === null || days === undefined) return 'default';
	if (days < 0) return 'error'; // Expired
	if (days <= 7) return 'warning'; // Less than a week
	if (days <= 30) return 'processing'; // Less than a month
	return 'success'; // More than a month
};

// Function to get status text
const getStatusText = (days, t) => {
	if (days === null || days === undefined) return t('header.subscription_unknown');
	if (days < 0) return t('header.subscription_expired');
	if (days === 0) return t('header.subscription_expires_today');
	if (days === 1) return t('header.subscription_expires_tomorrow');
	if (days <= 7) return t('header.subscription_expiring_soon');
	return t('header.subscription_active');
};

const PageHeader = () => {
	const { t } = useI18n();
	const { theme, themeColor, toggleTheme } = useTheme();
	const globalRedux = useSelector((state) => state.globalRedux);
	const loginRedux = useSelector((state) => state.loginRedux);
	const [open, setOpen] = useState(false);
	const [logoutModalVisible, setLogoutModalVisible] = useState(false);
	const [switchingBranch, setSwitchingBranch] = useState(false);
	const [selectedBranchName, setSelectedBranchName] = useState('');
	const themeToggleRef = useRef(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux.email);
	const displayName = [loginRedux?.firstName, loginRedux?.lastName].filter(Boolean).join(' ') || loginRedux?.email;
	const brandEyebrow = isSuperAdmin ? t('header.admin_console') : t('header.workspace');
	const profileDetail = loginRedux?.mobile || loginRedux?.email || t('header.no_mobile');

	// Calculate remaining days
	const remainingDays = useMemo(() => {
		return calculateRemainingDays(loginRedux?.nextBillDate);
	}, [loginRedux?.nextBillDate]);

	// Format date with locale support
	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const showDrawer = () => setOpen(true);
	const onClose = () => setOpen(false);

	const handleLogoutClick = () => {
		onClose();
		setLogoutModalVisible(true);
	};

	const handleLogoutConfirm = () => {
		setLogoutModalVisible(false);
		applicationLogout();
	};

	const handleLogoutCancel = () => {
		setLogoutModalVisible(false);
	};

	const handleBranchSwitch = (org) => {
		setSelectedBranchName(org?.branchName);
		setSwitchingBranch(true);
		dispatch(setSelectedBranch(org));
		setTimeout(() => {
			window.location.reload();
		}, 1500);
	};

	const isDarkTheme = theme === 'dark';

	const handleThemeToggle = () => {
		const rect = themeToggleRef.current?.getBoundingClientRect();
		const x = rect ? rect.left + rect.width / 2 : window.innerWidth - 160;
		const y = rect ? rect.top + rect.height / 2 : 40;

		document.documentElement.style.setProperty('--theme-toggle-x', `${x}px`);
		document.documentElement.style.setProperty('--theme-toggle-y', `${y}px`);

		if (document.startViewTransition) {
			document.startViewTransition(() => toggleTheme());
		} else {
			toggleTheme();
		}
	};

	const organizationMenuItems = globalRedux?.branches?.map((org) => ({
		key: org?._id,
		label: org?.branchName,
		onClick: () => handleBranchSwitch(org),
	}));

	const drawerMenuItems = [
		{
			key: 'profile',
			icon: <FiUser />,
			label: t('navigation.profile'),
			onClick: () => {
				onClose();
				navigate('/profile');
			},
		},
		{
			key: 'password-setup',
			icon: <FiLock />,
			label: t('navigation.password_setup'),
			onClick: () => {
				onClose();
				navigate('/password-setup');
			},
		},
		{
			key: 'settings',
			icon: <FiSettings />,
			label: t('navigation.settings'),
			onClick: () => {
				onClose();
				navigate('/settings');
			},
		},
	];

	// Add SIDE_MENUS items
	const sideMenuItems = SIDE_MENUS.map((menu) => ({
		key: menu?.key,
		icon: menu?.icon || <MailOutlined />,
		label: getMenuTranslation(menu?.name, t), // Use translation function
		onClick: () => {
			onClose();
			menu?.route && navigate(menu?.route);
		},
	}));

	const allDrawerMenuItems = [
		...drawerMenuItems,
		...sideMenuItems,
	];

	return loginRedux.isLogged ? (
		<>
			{/* Branch Switching Loader */}
			{switchingBranch && (
				<>
					<style>
						{`
							@keyframes pulse {
								0%, 100% {
									transform: scale(1);
									box-shadow: 0 8px 24px ${themeColor}40;
								}
								50% {
									transform: scale(1.05);
									box-shadow: 0 12px 32px ${themeColor}60;
								}
							}
						`}
					</style>
					<div
						className="branch-switching-overlay"
						style={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: theme === 'dark' 
								? 'rgba(20, 20, 20, 0.98)' 
								: 'rgba(255, 255, 255, 0.98)',
							backdropFilter: 'blur(12px)',
							zIndex: 99999,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '24px',
							transition: 'background-color 0.3s ease',
						}}>
						<div
							className="branch-switching-icon"
							style={{
								width: '80px',
								height: '80px',
								borderRadius: '50%',
								backgroundColor: themeColor,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								boxShadow: `0 8px 24px ${themeColor}40`,
								animation: 'pulse 2s ease-in-out infinite',
							}}>
							<SwapOutlined style={{ fontSize: '36px', color: 'white' }} spin />
						</div>
						<div
							style={{
								textAlign: 'center',
								maxWidth: '400px',
							}}>
							<div
								style={{
									fontSize: '24px',
									fontWeight: '600',
									color: 'var(--text-color)',
									marginBottom: '12px',
								}}>
								{t('header.switching_branch')}
							</div>
							<div
								style={{
									fontSize: '16px',
									color: 'var(--text-secondary)',
									marginBottom: '8px',
								}}>
								{t('header.switching_to')} <span style={{ fontWeight: '600', color: themeColor }}>{selectedBranchName}</span>
							</div>
							<div
								style={{
									fontSize: '14px',
									color: 'var(--text-secondary)',
								}}>
								{t('header.loading_branch_data')}
							</div>
							<div style={{ marginTop: '24px' }}>
								<Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: themeColor }} spin />} />
							</div>
						</div>
					</div>
				</>
			)}

			<AntHeader
				// title={
				// 	<div className="page-header-brand">
				// 		<div className="page-header-brand__content">
				// 			<div className="page-header-brand__title-row">
				// 				<span className="page-header-brand__title">FITTE</span>
				// 				<span className="page-header-brand__title-accent">.in</span>
				// 			</div>
				// 			<span className="page-header-brand__eyebrow">{brandEyebrow}</span>
				// 		</div>
				// 	</div>
				// }
				className="page-header-wrapper"
				style={{ '--header-accent': themeColor }}
				extra={
					<div className="header-extra-content">
						{/* Subscription Remaining Days Badge */}
						{remainingDays !== null && (
							<Tooltip
								title={
									<div style={{ textAlign: 'center', padding: '4px 0' }}>
										<div style={{ fontWeight: '600', marginBottom: '6px', color: '#ffffff' }}>
											{getStatusText(remainingDays, t)}
										</div>
										{loginRedux?.nextBillDate && (
											<div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.72)' }}>
												{t('header.next_bill_date')}: {formatDate(loginRedux.nextBillDate)}
											</div>
										)}
									</div>
								}
								overlayStyle={{ maxWidth: '250px' }}
								overlayInnerStyle={{
									background: 'rgba(30, 30, 40, 0.92)',
									border: '1px solid rgba(255, 255, 255, 0.12)',
									borderRadius: '8px',
								}}>
								<div 
									className="subscription-badge"
									style={{
										'--badge-color': remainingDays < 0 
											? 'var(--error-color, #ff4d4f)' 
											: remainingDays <= 7 
												? 'var(--warning-color, #faad14)' 
												: remainingDays <= 30 
													? themeColor 
													: 'var(--success-color, #52c41a)',
									}}>
									<div className="subscription-badge-content">
										<CalendarOutlined className="subscription-icon" />
										<span className="days-count">
											{remainingDays < 0 ? '!' : remainingDays}
										</span>
										<span className="subscription-label">{t('header.days_remaining')}</span>
									</div>
								</div>
							</Tooltip>
						)}
						
						<SecurityTimer />

						<Tooltip title={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}>
							<button
								ref={themeToggleRef}
								type="button"
								className={`theme-cloud-toggle ${isDarkTheme ? 'is-dark' : 'is-light'}`}
								onClick={handleThemeToggle}
								aria-label={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
							>
								<span className="theme-cloud-track">
									<span className="theme-cloud-glow" />
									<span className="theme-cloud-icon">
										{isDarkTheme ? <CloudMoon size={18} /> : <CloudSun size={18} />}
									</span>
								</span>
							</button>
						</Tooltip>
						
						{isSuperAdmin ? (
							<button className="page-header-profile-trigger" type="button" onClick={showDrawer}>
								<div className="page-header-profile-trigger__meta">
									<span className="page-header-profile-trigger__name">{displayName}</span>
									<span className="page-header-profile-trigger__detail">{profileDetail}</span>
								</div>
								<Avatar
									className="header-avatar pointer-icons"
									style={{ backgroundColor: themeColor }}
									size="large">
									{getFirstLetterFromWords(`${loginRedux?.firstName} ${loginRedux?.lastName}`)}
								</Avatar>
							</button>
						) : (
							<>
								<Dropdown menu={{ items: organizationMenuItems }} trigger={['click']}>
									<button className="branch-selector-button" type="button">
										<span className="branch-selector-button__label">{t('header.branch')}</span>
										<span className="branch-selector-button__value">{globalRedux?.selectedBranch?.branchName || t('header.no_branch_selected')}</span>
										<DownOutlined style={{ fontSize: '12px', transition: 'transform 0.2s' }} />
									</button>
								</Dropdown>
								<button className="page-header-profile-trigger" type="button" onClick={showDrawer}>
									<div className="page-header-profile-trigger__meta">
										<span className="page-header-profile-trigger__name">{displayName}</span>
										<span className="page-header-profile-trigger__detail">{profileDetail}</span>
									</div>
									<Avatar
										className="header-avatar pointer-icons"
										style={{ backgroundColor: themeColor }}
										size="large">
										{getFirstLetterFromWords(`${loginRedux?.firstName} ${loginRedux?.lastName}`)}
									</Avatar>
								</button>
							</>
						)}
					</div>
				}
			/>
			<Drawer
				rootClassName="app-drawer"
				title={
					<div className="drawer-profile-card">
						<div className="drawer-profile-card__avatar-wrap">
							<Avatar
								size={54}
								style={{
									backgroundColor: themeColor,
									boxShadow: `0 10px 24px ${themeColor}30`,
								}}>
								{getFirstLetterFromWords(`${loginRedux?.firstName} ${loginRedux?.lastName}`)}
							</Avatar>
						</div>
						<div className="drawer-profile-card__content">
							<div className="drawer-profile-card__eyebrow">{t('navigation.profile')}</div>
							<div className="drawer-profile-card__name">
								{loginRedux?.firstName} {loginRedux?.lastName}
							</div>
							<div className="drawer-profile-card__detail">{profileDetail}</div>
							{!isSuperAdmin && globalRedux?.selectedBranch?.branchName ? (
								<div className="drawer-profile-card__badge">{globalRedux.selectedBranch.branchName}</div>
							) : null}
						</div>
					</div>
				}
				placement="right"
				width="min(380px, 92vw)"
				open={open}
				onClose={onClose}
				style={{
					backgroundColor: 'var(--card-bg)',
				}}
				classNames={{
					body: 'app-drawer__body',
				}}
				headerStyle={{
					backgroundColor: 'var(--card-bg)',
					borderBottom: '1px solid var(--border-color)',
				}}
				bodyStyle={{
					backgroundColor: 'var(--card-bg)',
				}}>
				<div className="drawer-shell">
					<div className="drawer-shell__section drawer-shell__section--menu">
						<div className="drawer-shell__section-label">{t('header.workspace')}</div>
						<div className="drawer-container">
							<Menu
								mode="inline"
								items={allDrawerMenuItems.map((item) => ({
									key: item.key,
									icon: item.icon,
									label: item.label,
									onClick: item.onClick,
								}))}
							/>
						</div>
					</div>
					<div className="drawer-shell__footer">
						<button className="drawer-logout-button" type="button" onClick={handleLogoutClick}>
							<FiLogOut />
							<span>{t('navigation.logout')}</span>
						</button>
					</div>
				</div>
			</Drawer>

			<Modal
				title={
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							fontSize: '18px',
							fontWeight: '600',
							color: 'var(--text-color)',
						}}>
						<div
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								backgroundColor: 'var(--error-background)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}>
							<FiLogOut style={{ fontSize: '20px', color: 'var(--error-color)' }} />
						</div>
						{t('header.confirm_logout')}
					</div>
				}
				open={logoutModalVisible}
				onOk={handleLogoutConfirm}
				onCancel={handleLogoutCancel}
				okText={t('header.logout')}
				cancelText={t('header.cancel')}
				okButtonProps={{
					danger: true,
					style: {
						borderRadius: '6px',
						fontWeight: '500',
					},
				}}
				cancelButtonProps={{
					style: {
						borderRadius: '6px',
						fontWeight: '500',
					},
				}}
				centered
				width={420}
				closable={false}
				style={{ borderRadius: '12px' }}
				bodyStyle={{
					padding: '24px',
					borderRadius: '12px',
					backgroundColor: 'var(--card-bg)',
				}}
				maskStyle={{
					backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.45)',
				}}>
				<div style={{ textAlign: 'center' }}>
					<div
						style={{
							fontSize: '16px',
							color: 'var(--text-color)',
							marginBottom: '8px',
							fontWeight: '500',
						}}>
						{t('header.logout_confirmation')}
					</div>
					<div
						style={{
							fontSize: '14px',
							color: 'var(--text-secondary)',
							lineHeight: '1.5',
						}}>
						{t('header.logout_description')}
					</div>
					<div
						style={{
							marginTop: '20px',
							padding: '16px',
							backgroundColor: 'var(--success-background)',
							border: '1px solid var(--success-border)',
							borderRadius: '8px',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
						}}>
						<div
							style={{
								width: '20px',
								height: '20px',
								borderRadius: '50%',
								backgroundColor: 'var(--success-color, #52c41a)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}>
							<span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
						</div>
						<div style={{ textAlign: 'left' }}>
							<div style={{ fontSize: '13px', color: 'var(--success-color, #52c41a)', fontWeight: '500' }}>{t('header.session_end_message')}</div>
							<div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t('header.data_save_message')}</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	) : null;
};

export default PageHeader;
